/* eslint-disable no-console */
import _get from 'lodash/get';
import Store from 'lightningjs-redux';
import { featureFlipping, advertising } from '@bedrock-rdk/config';
import { storage } from '@bedrock-rdk/utils';
import { adEvents } from '@bedrock/ad-engine';
import { getAdPlayerInstance } from '@bedrock-rdk/media-engine';
import {
  adBreakStarted,
  adBreakEnded,
  adStarted,
  adPlaying,
  adJingleClipPlaying,
  adEnded,
  adError,
  adTimeUpdate,
  AD_JINGLE_POSITION,
} from '../../../logic/player/player.constants';
import { errorAction } from '../../../logic/error/error.actions';
import { ERROR_LEVELS, ERROR_TYPES } from '../../../logic/error/error.constants';
import {
  bridgeClassByProviderId,
  createMirroredAdEventHandlers,
  createRenderers,
  getAdsDurationInSeconds,
  updateQueryParams,
} from './ads.utils';
import { sendEvent, setAttributes } from '../../../logic/analytics/analytics.utils';
import { AD_ANALYTICS_TYPES, DEFAULT_VOLUME_LEVEL } from './ads.constants';
import { showAdJingle } from '../../../logic/player/player.actions';
import {
  getAdsProvider,
  getDisplayedAdBreakType,
  getDurationInSeconds,
  getIsAdError,
  getLBannerState,
  getVideoAds,
} from '../../../logic/player/player.selectors';
import { handleAdPlayerScale } from './platform/default/lBanner.utils';

/**
 * Responsible for initialization of a proper ad bridge instance, and for its control during the ad breaks
 */
export class AdsAdapter {
  /**
   * Initializes the ad pipeline of the current content, see `requestAds` to run it
   *
   * @param {Function} onContentPlay called by the ad-engine once the content can play (i.e. there is no pre-roll)
   * @returns {Promise<boolean>} whether the pipeline is ready, false when the content has no ads or they failed
   */
  async start(onContentPlay) {
    const state = Store.getState();
    const duration = getDurationInSeconds(state);
    this._contentDuration = duration;
    const providerConfig = getVideoAds(state);
    const providerId = getAdsProvider(state);

    // NOTE: Use this approach to use local "ads server"
    /*
    const host = `${window.location.href
      .replace(/\/index\.html.*$/, '')
      .replace(/\/#.*$/, '')}/static/assets/ads/VMAP.xml`;
    const localProviderConfig = { host, targeting: {} };
    localProviderConfig.jingles = {
      'before-pre-roll': [
        {
          id: 'ads_01',
          sequence_index: 1,
          url: 'static/assets/ads/jingle-before-13s.mp4',
        },
      ],
      'after-pre-roll': [
        {
          id: 'ads_03',
          sequence_index: 1,
          url: 'static/assets/ads/jingle-after-5s.mp4',
        },
      ],
      'before-mid-roll': [
        {
          id: 'ads_04',
          sequence_index: 1,
          url: 'static/assets/ads/jingle-before-13s.mp4',
        },
      ],
      'after-mid-roll': [
        {
          id: 'ads_05',
          sequence_index: 1,
          url: 'static/assets/ads/jingle-after-5s.mp4',
        },
      ],
    };
    providerId = 'fake';
    providerConfig = localProviderConfig;
    */

    if (!providerConfig || !providerConfig.host) {
      return false;
    }

    try {
      const result = await this.init({
        providerId,
        providerConfig,
        contentDuration: duration,
        contentHandlers: {
          playerPlay: onContentPlay,
          jingleDisplay: type => Store.dispatch(showAdJingle(type)),
          playerPause: () => {},
        },
      });

      if (!result) {
        return false;
      }
    } catch (error) {
      console.error('[player.ads.adapter] Could not initialize the ads', error);

      return false;
    }

    return true;
  }

  requestAds() {
    this._adBridge.requestAds({}, { start: 0, duration: this._contentDuration });
  }

  get bridge() {
    return this._adBridge || null;
  }

  /**
   * @returns {boolean} whether the ads handled the command, they don't once failed
   */
  play() {
    if (!this._adBridge || getIsAdError(Store.getState())) {
      return false;
    }

    try {
      this._adBridge.play();
    } catch (error) {
      console.error('[player.ads.adapter] Could not play', error);
    }

    return true;
  }

  /**
   * @returns {boolean} whether the ads handled the command, they don't once failed
   */
  pause() {
    if (!this._adBridge || getIsAdError(Store.getState())) {
      return false;
    }

    this._adBridge.pause();

    return true;
  }

  /**
   * Creates ad bridge instance correspondent to provided settings
   *
   * @param {Object} settings
   * @param {Object} settings.contentHandlers key-value map containing ad bridge callback handlers
   * @param {Number} settings.contentDuration duration of a video
   * @param {string} settings.providerId ads provider id, as given by the Layout API
   * @param {Object} settings.providerConfig advertising data defined for a specific provider, as given by the Layout API
   * @returns {Object}
   */
  async init({ contentHandlers, contentDuration, providerId, providerConfig: originalConfig }) {
    this.resetReportedAdErrors();
    // NOTE: ensuring empty config exists to bootstrap the player's routine
    const providerConfig = originalConfig ? { ...originalConfig } : {};
    const { brParams } = providerConfig;
    providerConfig.capping = (brParams && brParams.capping) || providerConfig.capping;
    providerConfig.jingles = (brParams && brParams.jingles) || providerConfig.jingles || {};
    if (featureFlipping.player_adJingles !== true) {
      providerConfig.jingles = {};
    }
    providerConfig.queryParams = updateQueryParams(providerConfig);

    const Bridge = bridgeClassByProviderId(providerId);

    if (!Bridge) {
      return null;
    }

    const { Engine: engine } = await getAdPlayerInstance();

    const eventHandlers = {
      ...createMirroredAdEventHandlers(),
      onBreakStarted: event => {
        console.log('[player.ads.adapter] Ad break started');
        this.mountRenderers();
        // Reset the per-break flag used to tell a "before" jingle from an "after" one.
        this._adStartedInBreak = false;
        const { type, silent } = event || {};

        if (!silent) {
          const adBreak = _get(this._adBridge, 'engine.currentSlot');
          Store.dispatch(adBreakStarted(type, this._adBridge.getSlotsDuration() || getAdsDurationInSeconds(adBreak)));
        }

        const analyticsKey = _get(AD_ANALYTICS_TYPES.begin, type, null);
        if (analyticsKey) {
          const engine = _get(this._adBridge, 'engine');
          const adCount = Array.isArray(event.creativeIds) ? event.creativeIds.length : undefined;

          setAttributes({
            adBreakId: event.reference,
            adGivenBreaks: engine && engine.getSlots ? engine.getSlots().length : undefined,
            adBreaksTime: this._adBridge.getSlotsDuration ? this._adBridge.getSlotsDuration() : undefined,
            adExpectedAds: adCount,
            adGivenAds: adCount,
          });
          this._currentAdBreakEvent = analyticsKey;
        }
      },
      onBreakEnded: () => {
        console.log('[player.ads.adapter] Ad break ended');
        this._adStartedInBreak = false;
        // NOTE: for some reason the onSlotEnded callback in the ad engine is not always working
        if (this._adBridge) {
          this._adBridge.endedAdsDuration = 0;
        }
        this.resetReportedAdErrors();
        const type = getDisplayedAdBreakType(Store.getState());
        Store.dispatch(adBreakEnded());
        this.unmountRenderers();

        const analyticsKey = _get(AD_ANALYTICS_TYPES.post, type, null);
        if (analyticsKey) {
          sendEvent({ key: analyticsKey });
        }
      },
      onError: error => {
        this.onAdError(error);
      },
      onStarted: event => {
        this.notifyAdBreakStarted();
        // A real ad has played in this break - any jingle after this is an "after" one.
        this._adStartedInBreak = true;
        this.resetReportedAdErrors();

        const { adId, duration, skipOffset, title, creativeId } = event;

        console.log(`[player.ads.adapter] Ad ${adId} started with duration of ${duration} seconds`);

        const engine = _get(this._adBridge, 'engine');
        const asset = (engine && engine.getCurrentAsset ? engine.getCurrentAsset() : null) || {};

        // Sending analytics
        setAttributes({
          adId,
          adDuration: duration,
          adTitle: title,
          adCreativeId: creativeId,
          adBitrate: asset.bitrate,
          adWidth: asset.width,
          adHeight: asset.height,
          adResource: asset.src,
          adSkippable: Number.isFinite(skipOffset),
        });
        sendEvent({ key: 'adStarted' });

        Store.dispatch(adStarted(skipOffset));
        // NOTE: first "playing" event is interpreted as ad start by the ad-engine
        Store.dispatch(adPlaying());
      },
      onJingleStarted: event => {
        console.log('[player.ads.adapter] Jingle::onJingleStarted:', event);

        setAttributes({
          adId: event?.target?.id,
          adDuration: undefined,
          adTitle: undefined,
          adCreativeId: undefined,
          adBitrate: undefined,
          adWidth: undefined,
          adHeight: undefined,
          adResource: event?.target?.url,
          adSkippable: false,
        });
        sendEvent({ key: 'adStarted' });
        this._isJingleStarted = true;

        // A jingle clip shares the ads player but is not a real ad
        const position = this._adStartedInBreak ? AD_JINGLE_POSITION.AFTER : AD_JINGLE_POSITION.BEFORE;
        Store.dispatch(adJingleClipPlaying(position));
      },
      onJingleError: event => {
        console.log('[player.ads.adapter] Jingle::onJingleError:', event);

        // The ad-engine resets a failed jingle without a stop event, the started ad view must be closed
        if (this._isJingleStarted) {
          this.notifyJingleEnded();
        }
      },
      onJingleStopped: event => {
        console.log('[player.ads.adapter] Jingle::onJingleStopped:', event);

        this.notifyJingleEnded();
      },
    };

    this._anEngineListeners = {
      [adEvents.REQUEST_COMPLETE]: this.onManifestResponse.bind(this),
      [adEvents.NO_DURATION_IN_CREATIVE]: this.onAdError.bind(this),
    };

    this._renderers = createRenderers('ads');
    // NOTE: the ads renderers are mounted for the duration of an ad break only
    this.unmountRenderers();
    const { video: videoRef, container: containerRef, companion: companionRef } = this._renderers;
    videoRef.autoplay = true;

    const adBridgeConfig = {
      ...providerConfig,
      contentDuration,
      features: {
        lazy: Boolean(featureFlipping.player_adLazyVmap),
        timeouts: advertising?.timeouts || {},
        triggerSlotOnSeek: {
          enabled: Boolean(featureFlipping.player_antiFF),
          preventDelayWhenFutureBreak: 10,
        },
      },
      volume: providerConfig.volume || DEFAULT_VOLUME_LEVEL,
    };
    const adEngineConfig = {
      VideoEngine: engine,
      storage,
    };
    const bridge = new Bridge(adBridgeConfig, eventHandlers, contentHandlers, adEngineConfig);

    bridge.setContainerRef(containerRef);
    bridge.setCompanionRef(companionRef);
    bridge.setVideoRef(videoRef);
    bridge.contentHasStarted();

    this._adTimeUpdateHandler = this.onAdTimeUpdate.bind(this);
    this._renderers.video.addEventListener('timeupdate', this._adTimeUpdateHandler);
    this._adBridge = bridge;
    this._manifestLoaded = false;

    await this._adBridge.init();

    Object.entries(this._anEngineListeners).forEach(([k, v]) => {
      bridge.engine.addEventListener(k, v);
    });

    console.log('[player.ads.adapter] Ad engine:', this._adBridge.engine);

    return {
      bridge,
    };
  }

  mountRenderers() {
    const container = _get(this._renderers, 'container');

    if (container && !container.parentNode) {
      document.body.appendChild(container);
      // The L-Banner state could have changed while the ads video was detached
      handleAdPlayerScale(Boolean(getLBannerState(Store.getState())));
    }
  }

  unmountRenderers() {
    const container = _get(this._renderers, 'container');

    if (container && container.parentNode) {
      container.parentNode.removeChild(container);
    }
  }

  onManifestResponse(response) {
    if (!this._adBridge) {
      return;
    }
    console.log('[player.ads.adapter] Manifest response:', response);

    // A failed follow-up (per-break) request must not turn later ad errors into manifest errors
    this._manifestLoaded = this._manifestLoaded || response.success;
    if (response.success) {
      sendEvent({ key: 'adManifest', success: response.success, adBreaks: response.slots?.length ?? 0 });
    }
  }

  onAdError(error) {
    console.error('[player.ads.adapter] Ads error:', error);
    if (!error) {
      return;
    }

    Store.dispatch(adError(error));

    if (!this.shouldReportAdError(error)) {
      return;
    }

    const ad = _get(error, 'content.ad', {});
    const adResource = _get(ad, 'mediaFile.url');
    const adErrorMetadata = [adResource, error.adRequestUrl].filter(Boolean);
    setAttributes({
      adErrorCode: error.details,
      adErrorMessage: error.reason,
      adErrorFatal: Boolean(error.fatal),
      adErrorCreativeId: ad.creativeId,
      adErrorResource: adResource,
      adErrorMetadata: adErrorMetadata.length ? adErrorMetadata : undefined,
    });

    if (this._manifestLoaded) {
      sendEvent({ key: 'adError' });
    } else {
      sendEvent({ key: 'adManifest', success: false, errorCode: error.details, requestUrl: error.adRequestUrl });
      // TODO: figure out if we should dispatch a playback error, ex:
      // setAttributes({
      //   // This error code exists for a sake of consistency. It's not used in Shaka, however fails under the ads error numbers (10000+).
      //   errorCode: 10001,
      //   errorMessage: 'Failed to load ad manifest',
      //   errorMetadata: [_get(this._adBridge, 'config.host')].filter(Boolean),
      //   errorFatal: false,
      // });
      // sendEvent({ key: 'playbackError' });
    }
  }

  // The ad-engine may report one failure several times (e.g. parser error stack + a code-less follow-up)
  shouldReportAdError(error) {
    // Errors the ad-engine flags as expected (e.g. programmatic no-fill) are not monitored
    if (error.monitored === false) {
      return false;
    }

    const adId = _get(error, 'content.ad.id');
    const key = `${error.details}|${error.reason}|${adId}`;
    const isFollowUp =
      error.details === undefined && this._reportedAdErrors.size > 0 && this._lastReportedAdErrorAdId === adId;

    if (isFollowUp || this._reportedAdErrors.has(key)) {
      return false;
    }

    this._reportedAdErrors.add(key);
    this._lastReportedAdErrorAdId = adId;

    return true;
  }

  resetReportedAdErrors() {
    this._reportedAdErrors = new Set();
    this._lastReportedAdErrorAdId = undefined;
  }

  notifyJingleEnded() {
    this._isJingleStarted = false;
    Store.dispatch(adEnded());
    sendEvent({ key: 'adComplete' });
  }

  notifyAdBreakStarted() {
    if (this._currentAdBreakEvent) {
      sendEvent({ key: this._currentAdBreakEvent });
      this._currentAdBreakEvent = undefined;
    }
  }

  onAdTimeUpdate(event) {
    if (!this._adBridge || !event.target || !Number.isFinite(event.target.currentTime)) {
      return;
    }

    const { currentTime } = event.target;
    Store.dispatch(adTimeUpdate(currentTime, currentTime + this._adBridge.getEndedAdsDuration()));
  }

  reset() {
    if (this._adBridge) {
      try {
        Object.entries(this._anEngineListeners).forEach(([k, v]) => {
          this._adBridge.engine.removeEventListener(k, v);
        });
      } catch (error) {
        Store.dispatch(errorAction(ERROR_LEVELS.SILENT, ERROR_TYPES.GENERAL, error.message));
      }

      try {
        this._adBridge.release();
      } catch (error) {
        Store.dispatch(errorAction(ERROR_LEVELS.SILENT, ERROR_TYPES.GENERAL, error.message));
      }
    }
    this._manifestLoaded = false;
    this._adBridge = null;
    this._currentAdBreakEvent = undefined;
    this._isJingleStarted = false;
    this.resetReportedAdErrors();

    if (this._renderers) {
      Object.values(this._renderers).forEach(renderer => {
        if (renderer && renderer.parentNode) {
          renderer.parentNode.removeChild(renderer);
        }
      });
      if (this._renderers.video) {
        this._renderers.video.removeEventListener('timeupdate', this._adTimeUpdateHandler);
      }
    }
    this._renderers = null;
    this._adTimeUpdateHandler = null;
  }
}
